import { Button } from '@/shared/components/ui/button'
import { cn } from '@/shared/lib/utils'

interface YesNoToggleProps {
  /** ``true`` = Yes, ``false`` = No, ``null`` = no choice made yet. */
  value: boolean | null
  onChange: (next: boolean) => void
  className?: string
  disabled?: boolean
}

/**
 * Sleek segmented Yes/No control with a sliding pill indicator.
 *
 * When ``value`` is ``null`` no pill is shown, so a required field still
 * forces an explicit choice. Selecting Yes slides a green pill into place;
 * selecting No slides a filled ink pill - both states read clearly as
 * "answered", with green reserved for the affirmative and a neutral fill for
 * the negative (no caution/error colour for a routine choice).
 */
export function YesNoToggle({
  value,
  onChange,
  className,
  disabled = false,
}: YesNoToggleProps) {
  return (
    <div
      role="radiogroup"
      className={cn(
        'relative inline-flex h-7 w-[104px] shrink-0 items-center rounded-full border-[0.5px] border-[var(--rule)] bg-[var(--bg-light)] p-0.5 text-[12px]',
        disabled && 'opacity-50',
        className,
      )}
    >
      {/* Sliding pill - only rendered once a choice exists. */}
      {value !== null && (
        <span
          aria-hidden
          className={cn(
            'absolute inset-y-0.5 left-0.5 w-[calc(50%-2px)] rounded-full shadow-sm transition-transform duration-200 ease-out',
            value
              ? 'translate-x-0 bg-[var(--green)]'
              : 'translate-x-full bg-[var(--ink)]',
          )}
        />
      )}
      <Button
        type="button"
        variant="ghost"
        role="radio"
        aria-checked={value === true}
        disabled={disabled}
        onClick={() => onChange(true)}
        className={cn(
          'relative z-10 h-auto flex-1 rounded-full bg-transparent p-0 py-1 text-center shadow-none transition-colors duration-200 hover:bg-transparent',
          value === true
            ? 'font-medium text-white'
            : 'text-[var(--ink-3)] hover:text-[var(--ink-2)]',
        )}
      >
        Yes
      </Button>
      <Button
        type="button"
        variant="ghost"
        role="radio"
        aria-checked={value === false}
        disabled={disabled}
        onClick={() => onChange(false)}
        className={cn(
          'relative z-10 h-auto flex-1 rounded-full bg-transparent p-0 py-1 text-center shadow-none transition-colors duration-200 hover:bg-transparent',
          value === false
            ? 'font-medium text-white'
            : 'text-[var(--ink-3)] hover:text-[var(--ink-2)]',
        )}
      >
        No
      </Button>
    </div>
  )
}
